import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

type Preset = 'inset' | 'circle' | 'ellipse' | 'triangle' | 'rhombus' | 'pentagon' | 'hexagon' | 'octagon' | 'star' | 'arrow' | 'speechBubble' | 'parallelogram' | 'trapezoid';

const PRESETS: Record<Preset, string> = {
    inset: 'inset(10% 10% 10% 10%)',
    circle: 'circle(45% at 50% 50%)',
    ellipse: 'ellipse(40% 50% at 50% 50%)',
    triangle: 'polygon(50% 0%, 0% 100%, 100% 100%)',
    rhombus: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
    pentagon: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
    hexagon: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
    octagon: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
    star: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
    arrow: 'polygon(40% 0%, 40% 20%, 100% 20%, 100% 80%, 40% 80%, 40% 100%, 0% 50%)',
    speechBubble: 'polygon(0% 0%, 100% 0%, 100% 75%, 75% 75%, 75% 100%, 50% 75%, 0% 75%)',
    parallelogram: 'polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)',
    trapezoid: 'polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)',
};

interface State {
    preset: Preset;
    customPath: string;
    bgColor: string;
    color: string;
    copied: boolean;
}

export default class ClipPathGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        preset: 'pentagon',
        customPath: '',
        bgColor: '#f8fafc',
        color: '#6366f1',
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get clipValue(): string {
        return this.state.customPath.trim() || PRESETS[this.state.preset];
    }

    private get cssCode(): string {
        return `clip-path: ${this.clipValue};`;
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { preset, customPath, bgColor, color, copied } = this.state;

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Preset shape</span></div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.4rem' }}>
                        {(Object.keys(PRESETS) as Preset[]).map(p => (
                            <button key={p}
                                className={`${genStyles.iconBtn} ${preset === p && !customPath ? genStyles.iconBtnPrimary : ''}`}
                                onClick={() => this.setState({ preset: p, customPath: '' })}>
                                {p}
                            </button>
                        ))}
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="cp-custom">Custom clip-path (overrides preset)</label>
                        <textarea id="cp-custom" rows={2} value={customPath} placeholder={PRESETS[preset]}
                            onChange={e => this.setState({ customPath: e.target.value })} spellCheck={false} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Colors</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="cp-shape">Shape</label>
                            <input id="cp-shape" type="color" value={color} style={{ height: 38, padding: 4 }}
                                onChange={e => this.setState({ color: e.target.value })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="cp-bg">Background</label>
                            <input id="cp-bg" type="color" value={bgColor} style={{ height: 38, padding: 4 }}
                                onChange={e => this.setState({ bgColor: e.target.value })} />
                        </div>
                    </div>
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                previewMeta={customPath ? 'custom' : preset}
                preview={
                    <div style={{ background: bgColor, padding: 24, borderRadius: 8, display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: 180, height: 180, background: color, clipPath: this.clipValue }} />
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
